/**
 * DeployDialog — SideSheet-hosted form for deploying a profile version.
 *
 * This is the single chokepoint for promoting a profile across the deploy
 * FSM (shadow → canary → full — ADR-100 Appendix A). Reason is required
 * per ECTP Rule 7 and is written to the deployment audit trail.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { Rocket } from '@phosphor-icons/react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '../../shared/ui/Button';
import { FieldError } from '../../shared/ui/FieldError';
import { Input } from '../../shared/ui/Input';
import { Label } from '../../shared/ui/Label';
import { Select } from '../../shared/ui/Select';
import { SideSheet } from '../../shared/ui/SideSheet';
import { Textarea } from '../../shared/ui/Textarea';
import { useDeployProfile } from '../../features/business-profiles/useBusinessProfiles';
import {
  deployFormSchema,
  type BusinessProfileRecord,
  type DeployFormValues,
} from '../../features/business-profiles/types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profile: BusinessProfileRecord;
  onDeployed?: () => void;
}

export function DeployDialog({
  open,
  onOpenChange,
  profile,
  onDeployed,
}: Props): JSX.Element {
  const { t } = useTranslation('adminProfiles');
  const deployMutation = useDeployProfile(profile.subVerticalKey);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<DeployFormValues>({
    resolver: zodResolver(deployFormSchema),
    defaultValues: {
      mode: 'shadow',
      tenantScope: 'all',
      reason: '',
    },
  });

  const mode = watch('mode');
  const tenantScope = watch('tenantScope');

  const onSubmit = async (values: DeployFormValues) => {
    await deployMutation.mutateAsync(values);
    reset();
    onOpenChange(false);
    onDeployed?.();
  };

  const translateError = (key: keyof DeployFormValues): string | undefined => {
    const e = errors[key];
    if (!e) return undefined;
    const msg = e.message;
    if (msg === 'percentageRequired') return t('deploy.percentageRequired');
    if (msg === 'tenantIdsRequired') return t('deploy.tenantIdsRequired');
    if (!msg) return t('deploy.required');
    return msg;
  };

  return (
    <SideSheet
      open={open}
      onOpenChange={onOpenChange}
      title={t('deploy.title', {
        displayName: profile.displayName,
        version: profile.profileVersion,
      })}
      description={t('deploy.subtitle')}
      closeLabel={t('deploy.cancel')}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-biz-content-lg" noValidate>
        <div>
          <Label htmlFor="deploy-mode">{t('deploy.modeLabel')}</Label>
          <Select id="deploy-mode" invalid={Boolean(errors.mode)} {...register('mode')}>
            <option value="shadow">{t('deploy.modes.shadow')}</option>
            <option value="canary">{t('deploy.modes.canary')}</option>
            <option value="full">{t('deploy.modes.full')}</option>
          </Select>
          <p className="mt-biz-content text-biz-xs text-biz-muted-foreground">
            {t(`deploy.modeHints.${mode}` as const)}
          </p>
        </div>

        <div>
          <Label htmlFor="deploy-scope">{t('deploy.scopeLabel')}</Label>
          <Select
            id="deploy-scope"
            invalid={Boolean(errors.tenantScope)}
            {...register('tenantScope')}
          >
            <option value="all">{t('deploy.scopes.all')}</option>
            <option value="percentage">{t('deploy.scopes.percentage')}</option>
            <option value="tenant_list">{t('deploy.scopes.tenant_list')}</option>
          </Select>
        </div>

        {tenantScope === 'percentage' ? (
          <div>
            <Label htmlFor="deploy-percentage">{t('deploy.percentageLabel')}</Label>
            <Input
              id="deploy-percentage"
              type="number"
              min={1}
              max={100}
              invalid={Boolean(errors.percentage)}
              {...register('percentage')}
            />
            <FieldError id="deploy-percentage-error">
              {translateError('percentage')}
            </FieldError>
          </div>
        ) : null}

        {tenantScope === 'tenant_list' ? (
          <div>
            <Label htmlFor="deploy-tenants">{t('deploy.tenantIdsLabel')}</Label>
            <Textarea
              id="deploy-tenants"
              placeholder={t('deploy.tenantIdsPlaceholder')}
              rows={3}
              invalid={Boolean(errors.tenantIds)}
              {...register('tenantIds')}
            />
            <FieldError id="deploy-tenants-error">
              {translateError('tenantIds')}
            </FieldError>
          </div>
        ) : null}

        <div>
          <Label htmlFor="deploy-reason">{t('deploy.reasonLabel')}</Label>
          <Textarea
            id="deploy-reason"
            rows={4}
            placeholder={t('deploy.reasonPlaceholder')}
            invalid={Boolean(errors.reason)}
            {...register('reason')}
          />
          <FieldError id="deploy-reason-error">
            {errors.reason?.message ? translateError('reason') : undefined}
          </FieldError>
          <p className="mt-biz-content text-biz-xs text-biz-muted-foreground">
            {t('deploy.reasonHint')}
          </p>
        </div>

        <div className="flex items-center justify-end gap-biz-content-lg pt-biz-content-lg border-t border-biz-border">
          <Button type="button" intent="secondary" onClick={() => onOpenChange(false)}>
            {t('deploy.cancel')}
          </Button>
          <Button
            type="submit"
            intent="primary"
            leftIcon={<Rocket size={18} weight="regular" />}
            loading={isSubmitting || deployMutation.isPending}
          >
            {t('deploy.submit')}
          </Button>
        </div>
      </form>
    </SideSheet>
  );
}
